import '../../styles/storyDetail.css';
import useStoryChatter from '../../hooks/useStoryChatter';
import ChatterPagination from './ChatterPagination';
import { useDynamicsPage } from '../../hooks/useDynamicsPage';
import { useDynamicsRecordLink } from '../../hooks/useDynamicsRecordLink';
import ClientFilters from '../common/ClientFilters';
import ClientPage from '../common/ClientPage';
import ThemeToggle from '../common/ThemeToggle';
import UserStoryBillingBadge from './UserStoryBillingBadge';
import AcceptanceCriteriaGrid from './AcceptanceCriteriaGrid';
import useAnimatedModalClose from '../../hooks/useAnimatedModalClose';
/**
 * Page User Stories d'un Jalon
 *
 * Affiche la liste des User Stories appartenant à un jalon donné
 */

import UserStoryAttachments from './UserStoryAttachments';
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Search, XCircle, MessageSquare, Send, User, Calendar } from 'lucide-react';
import { useProjectData } from '../../context/ProjectDataContext';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorMessage from '../common/ErrorMessage';
import Pagination from '../common/Pagination';
import backendApi from '../../services/backendApi';
import '../../styles/odooDescription.css';

const STAGE_COLORS = {
  'Done': 'bg-green-100 text-green-700',
  'Tests à valider': 'bg-yellow-100 text-yellow-700',
  'En Test': 'bg-blue-100 text-blue-700',
  'Confirmation': 'bg-orange-100 text-orange-700',
  'En cours': 'bg-indigo-100 text-indigo-700',
};

const MilestoneUserStoriesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { milestoneId, milestoneName } = location.state || {};

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSPs, setSelectedSPs] = useState(new Set());
  const [viewingUs, setViewingUs] = useState(null);
  const viewModalExit = useAnimatedModalClose(() => setViewingUs(null));
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // États chatter
  const [isSendingUserStoryMessage, setIsSendingUserStoryMessage] = useState(false);
  const [newUserStoryMessage, setNewUserStoryMessage] = useState('');

  const { isAdmin = false, userStories, loading, error: dataError, refresh } = useProjectData();

  // Filtrer les US du jalon
  const milestoneStories = useMemo(() => {
    if (!milestoneId) return [];
    return userStories.filter(us => us.milestoneId === milestoneId);
  }, [userStories, milestoneId]);

  // Valeurs SP uniques
  const uniqueSPs = useMemo(() => {
    const vals = new Set(milestoneStories.map(us => us.storyPoints || 0));
    return [...vals].sort((a, b) => a - b);
  }, [milestoneStories]);

  const toggleSP = (sp) => {
    setSelectedSPs(prev => {
      const next = new Set(prev);
      if (next.has(sp)) next.delete(sp); else next.add(sp);
      return next;
    });
  };

  // Filtrer par recherche + SP
  const filteredStories = useMemo(() => {
    let filtered = milestoneStories;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(us =>
        (us.name?.toLowerCase() || '').includes(query) ||
        (us.code?.toLowerCase() || '').includes(query)
      );
    }
    if (selectedSPs.size > 0) {
      filtered = filtered.filter(us => selectedSPs.has(us.storyPoints || 0));
    }
    return filtered;
  }, [milestoneStories, searchQuery, selectedSPs]);

  useEffect(() => { setCurrentPage(1); }, [searchQuery, selectedSPs]);

  const paginatedStories = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredStories.slice(start, start + itemsPerPage);
  }, [filteredStories, currentPage]);

  const totalPages = Math.ceil(filteredStories.length / itemsPerPage);

  // Chatter
  const chatter = useStoryChatter(viewingUs?.id);
  const { messages: userStoryMessages, loading: loadingUsMessages, reload: fetchMessages } = chatter;

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newUserStoryMessage.trim() || !viewingUs) return;
    setIsSendingUserStoryMessage(true);
    try {
      await backendApi.createUserStoryMessage(viewingUs.id, newUserStoryMessage);
      setNewUserStoryMessage('');
      await fetchMessages(viewingUs.id);
    } catch (error) {
      alert(error.message || 'Erreur lors de l\'envoi du message');
    } finally {
      setIsSendingUserStoryMessage(false);
    }
  };

  useDynamicsRecordLink(milestoneStories, setViewingUs);
  useDynamicsPage({path:'/dashboard/milestone-stories',title:viewingUs ? viewingUs.name : (milestoneName || 'Jalon'),kind:'tasks',recordIds:[...new Set([...paginatedStories.map(t => t.id), ...(viewingUs ? [viewingUs.id] : [])])],listRecordIds:paginatedStories.map(t => t.id),focusedRecordId:viewingUs?.id || null,filters:{search:searchQuery, storyPoints:[...selectedSPs]},ready:!loading});

  if (loading) return <div className="min-h-full bg-gray-50"><LoadingSpinner message="Chargement..." /></div>;
  if (dataError) return <div className="min-h-full bg-gray-50"><ErrorMessage message={dataError} onRetry={refresh} /></div>;

  return (
    <ClientPage>
      <div className="client-story-page">
        <div className="client-theme-control"><ThemeToggle /></div>
        {/* Header */}
        <div className="client-story-header mb-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-5 h-5 text-purple-500" />
                  <h1 className="text-2xl font-semibold text-gray-900">{milestoneName || 'Jalon'}</h1>
                </div>
                <p className="text-sm text-gray-500">
                  {filteredStories.length} user {filteredStories.length > 1 ? 'stories' : 'story'}
                </p>
              </div>
            </div>


          </div>
        </div>

        <ClientFilters title={milestoneName || 'Jalon'} summary={[selectedSPs.size > 0 && `${[...selectedSPs].join(', ')} SP`]} primary={<div aria-label="Recherche des User Stories">            {/* Barre de recherche */}
            <div className="relative w-96">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par titre ou code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-teal-500 focus:border-brand-teal-500 outline-none transition-colors"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              )}
            </div></div>}>
        {/* Chips filtre Story Points */}
        {uniqueSPs.length > 1 && (
          <div className="flex items-center gap-2 flex-wrap mb-4">
            <span className="text-xs font-medium text-gray-500 shrink-0">Story Points :</span>
            {uniqueSPs.map(sp => (
              <button
                key={sp}
                onClick={() => toggleSP(sp)}
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                  selectedSPs.has(sp)
                    ? 'bg-brand-green text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {sp} SP
              </button>
            ))}
            {selectedSPs.size > 0 && (
              <button
                onClick={() => setSelectedSPs(new Set())}
                className="text-xs text-gray-400 hover:text-gray-600 underline ml-1"
              >
                Effacer
              </button>
            )}
          </div>
        )}

</ClientFilters>
        {/* Liste */}
        {filteredStories.length === 0 ? (
          <div key={`${searchQuery}|${currentPage}|${[...selectedSPs].join(',')}|empty`} className="filter-results-motion bg-white rounded-lg border border-gray-200 p-12 text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune User Story</h3>
            <p className="text-sm text-gray-500">
              {searchQuery ? 'Essayez de modifier vos critères de recherche.' : 'Aucune user story associée à ce jalon.'}
            </p>
          </div>
        ) : (
          <div key={`${searchQuery}|${currentPage}|${[...selectedSPs].join(',')}`} className="filter-results-motion bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* En-tête du tableau */}
            <div className="bg-gray-50 border-b border-gray-200 px-6 py-3">
              <div className="flex items-center gap-4">
                <div className="w-32">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Numéro</span>
                </div>
                <div className="w-36">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Statut</span>
                </div>
                <div className="flex-1">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Titre</span>
                </div>
                <div className="w-40 text-center">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Estimation</span>
                </div>
              </div>
            </div>

            {/* Lignes */}
            <div className="divide-y divide-gray-200">
              {paginatedStories.map((us, index) => {
                const usCode = us.code || `US-${String(index + 1).padStart(2, '0')}`;
                const stageClass = STAGE_COLORS[us.category] || 'bg-gray-100 text-gray-600';
                return (
                  <div
                    key={us.id}
                    className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => setViewingUs(us)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-32">
                        <span className="text-xs font-semibold text-gray-500">{usCode}</span>
                      </div>
                      <div className="w-36">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${stageClass}`}>
                          {us.category}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-gray-900">{us.name}</h3>
                        {isAdmin && <UserStoryBillingBadge story={us} />}
                      </div>
                      <div className="w-40 text-center">
                        <span className="inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-semibold bg-brand-teal-100 text-brand-teal-700">
                          {us.storyPoints || 0} SP ({us.hourlyRate || 0}h)
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {filteredStories.length > itemsPerPage && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredStories.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
              />
            )}
          </div>
        )}

        {/* Modale détails */}
        {viewingUs && (
          <div className={`us-detail-overlay client-story-dialog ${viewModalExit.modalClassName} fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-6`} onClick={viewModalExit.close}>
            <div
              className="bg-white rounded-lg us-detail-window w-full overflow-hidden flex flex-col shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-start justify-between gap-4">
                  <h2 className="text-xl font-semibold text-gray-900 flex-1">{viewingUs.name}</h2>
                  {isAdmin && <UserStoryBillingBadge story={viewingUs} />}
                  <button
                    onClick={viewModalExit.close}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Corps */}
              <div className="client-story-dialog-body">
                {/* Détails */}
                <div className="flex-1 overflow-y-auto px-6 py-6">
                  <div className="mb-6 grid grid-cols-3 gap-6">
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">Numéro</h3>
                      <div className="text-base text-gray-700 font-semibold">{viewingUs.code || viewingUs.id}</div>
                    </div>
                    {viewingUs.milestoneName && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">Jalon</h3>
                        <div className="text-base text-gray-700 font-medium">{viewingUs.milestoneName}</div>
                      </div>
                    )}
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">Statut</h3>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${STAGE_COLORS[viewingUs.category] || 'bg-gray-100 text-gray-600'}`}>
                        {viewingUs.category}
                      </span>
                    </div>
                  </div>
                  <AcceptanceCriteriaGrid initialData={chatter.criteriaData} initialError={chatter.criteriaData ? null : chatter.error} task={viewingUs} fallback={viewingUs.description ? (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">Description</h3>
                      <div
                        className="text-base text-gray-700 prose prose-base max-w-none bg-gray-50 rounded-lg p-6 border border-gray-200"
                        dangerouslySetInnerHTML={{ __html: viewingUs.description }}
                      />
                    </div>
                  ) : null} />
                </div>

                {/* Chatter */}
                <div className="us-chatter-pane w-[520px] bg-gray-50 flex flex-col border-l border-gray-200">
                  <div className="px-6 pb-4 bg-white border-b border-gray-200 flex-shrink-0"><UserStoryAttachments taskId={viewingUs.id} /></div>
                  <div className="px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0">
                    <div className="flex items-center gap-2 mb-1">
                      <MessageSquare className="w-5 h-5 text-brand-purple" />
                      <h3 className="text-base font-bold text-gray-900">Chatter</h3>
                      <span className="text-sm text-gray-500">({userStoryMessages.length} sur cette page)</span>
                    </div>
                    <p className="text-sm text-gray-500">Historique des messages</p>
                  </div>

                  <div className="p-4 bg-white border-t border-gray-200 flex-shrink-0">
                    <form onSubmit={handleSendMessage} className="flex flex-col gap-2">
                      <textarea
                        value={newUserStoryMessage}
                        onChange={(e) => setNewUserStoryMessage(e.target.value)}
                        placeholder="Écrire un commentaire..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-brand-purple resize-none text-sm"
                        rows="3"
                        disabled={isSendingUserStoryMessage}
                      />
                      <div className="flex justify-end">
                        <button
                          type="submit"
                          disabled={isSendingUserStoryMessage || !newUserStoryMessage.trim()}
                          className="flex items-center gap-2 px-4 py-2 bg-brand-purple text-white rounded-lg hover:bg-brand-purple-dark transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSendingUserStoryMessage ? (
                            <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>Envoi...</>
                          ) : (
                            <><Send className="w-4 h-4" />Envoyer</>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                  <ChatterPagination chatter={chatter} />
                  <div className="chatter-message-list flex-1 overflow-y-auto p-6 pb-0">
                    {loadingUsMessages ? (
                      <div className="flex items-center justify-center py-16">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-purple"></div>
                      </div>
                    ) : userStoryMessages.length === 0 ? (
                      <div className="text-center py-16 bg-white rounded border border-gray-200">
                        <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-base text-gray-500 mb-2">Aucun message</p>
                        <p className="text-sm text-gray-400">Soyez le premier à commenter</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {userStoryMessages.map((message) => {
                          const isEmail = message.messageType === 'email';
                          return (
                            <div key={message.id} className={`rounded-lg p-4 border shadow-sm ${isEmail ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}`}>
                              <div className="flex items-start gap-3 mb-3">
                                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${isEmail ? 'bg-blue-200' : 'bg-brand-purple-lighter'}`}>
                                  {isEmail ? <span className="text-base">📧</span> : <User className="w-4 h-4 text-brand-purple" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-bold text-gray-900 truncate">{message.authorName}</p>
                                  <p className="text-sm text-gray-500">
                                    {message.date && new Date(message.date).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                  </p>
                                </div>
                              </div>

                              {message.body && (
                                <div className="text-sm text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: message.body }} />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>



                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex-shrink-0 bg-gray-50">
                <button
                  onClick={viewModalExit.close}
                  className="px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ClientPage>
  );
};

export default MilestoneUserStoriesPage;
